/**
 * XNGAGE CONFIDENTIAL
 * __________________________
 *
 * Copyright (C) 2023 Xngage - All Rights Reserved
 *
 * All code or information contained herein is, and remains the
 * property of Xngage LLC and its customers.  The intellectual
 * and technical concepts contained are proprietary to Xngage LLC
 * and may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Xngage LLC.
 */

import Modal, { ModalPresentationProps } from "@eisinc/MobiusOverrides/Modal";
import CheckmarkGylph from "@eisinc/XDL/Icons/CheckmarkGylph";
import WarningStock from "@eisinc/XDL/Icons/WarningStock";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import getRealTimeWarehouseInventory from "@insite/client-framework/Store/Data/RealTimeInventory/Handlers/GetRealTimeWarehouseInventory";
import { getCurrentShipToState, getShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import translate from "@insite/client-framework/Translate";
import {
    AvailabilityDto,
    AvailabilityMessageType,
    ProductSettingsModel,
    WarehouseDto,
} from "@insite/client-framework/Types/ApiModels";
import DataTable, { DataTableProps } from "@insite/mobius/DataTable";
import DataTableBody, { DataTableBodyProps } from "@insite/mobius/DataTable/DataTableBody";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableHead, { DataTableHeadProps } from "@insite/mobius/DataTable/DataTableHead";
import DataTableHeader, { DataTableHeaderProps } from "@insite/mobius/DataTable/DataTableHeader";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

interface AdditionalResultsProps {
    pickupMessage?: string;
    isInStockAtPickUpWarehouse?: string;
    inventoryFallBack?: string;
    hideCheckAllWarehouseHyperLink?: string;
}

interface OwnProps {
    productId: string;
    availability?: AvailabilityDto;
    unitOfMeasure: string;
    trackInventory: boolean;
    isProductDetailsPage?: boolean;
    extendedStyles?: ProductAvailabilityStyles;
    failedToLoadInventory?: boolean;
    additionalResults: AdditionalResultsProps;
}

const mapStateToProps = (state: ApplicationState) => ({
    productSettings: getSettingsCollection(state).productSettings,
    customSettings: getSettingsCollection(state).customSettings as any,
    currentShipTo: getShipToState(state, getSession(state).shipToId).value,
    showInventory: state.context.showInventory,
});

const mapDispatchToProps = {
    getRealTimeWarehouseInventory,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductAvailabilityStyles {
    container?: InjectableCss;
    messageGridItem?: InjectableCss;
    availabilityByWarehouseLinkGridItem?: InjectableCss;
    inventoryMessage?: InjectableCss;
    messageText?: TypographyPresentationProps;
    realTimeText?: TypographyPresentationProps;
    errorWrapper?: InjectableCss;
    errorText?: TypographyPresentationProps;
    availabilityByWarehouseLink?: LinkPresentationProps;
    availabilityByWarehouseModal?: ModalPresentationProps;
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    availabilityByWarehouseTable?: DataTableProps;
    availabilityByWarehouseTableHead?: DataTableHeadProps;
    availabilityByWarehouseTableHeader?: DataTableHeaderProps;
    availabilityByWarehouseTableBody?: DataTableBodyProps;
    availabilityByWarehouseTableRow?: DataTableRowProps;
    availabilityByWarehouseTableCell?: DataTableCellBaseProps;
    pickupMessage?: TypographyPresentationProps;
    containerWithAdditionalMessage?: InjectableCss;
    additionalMessageContainer?: InjectableCss;
    separatorStyle?: InjectableCss;
    tableContainer?: InjectableCss;
}

export const productAvailabilityStyles: ProductAvailabilityStyles = {
    container: {
        css: css`
            display: flex;
            flex-direction: column;
        `,
    },
    tableContainer: {
        css: css`
            overflow: auto;
        `,
    },
    containerWithAdditionalMessage: {
        css: css`
            display: flex;
            @media only screen and (max-width: 769px) {
                flex-direction: column;
            }
        `,
    },
    additionalMessageContainer: {
        css: css`
            display: flex;
            flex-direction: column;

            > div {
                line-height: 18px;
            }
        `,
    },
    messageGridItem: {},
    availabilityByWarehouseLinkGridItem: {},
    availabilityByWarehouseModal: {
        size: 1000,
        cssOverrides: {
            modalContainer: css`
                overflow: auto;
                white-space: nowrap;
            `,
        },
    },
    availabilityByWarehouseTable: {
        css: css`
            width: 100%;
            overflow: auto;
        `,
    },
    centeringWrapper: {
        css: css`
            height: 100px;
            width: 100%;
            display: flex;
            align-items: center;
        `,
    },
    spinner: {
        color: "primary.main",
        css: css`
            margin: 0 auto;
        `,
    },
    messageText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
        css: css`
            @media print {
                line-height: 16px;
                font-size: 12px;
                white-space: pre-wrap;
            }
        `,
    },
    realTimeText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
        css: css`
            display: inline-block;
            text-align: left;
            min-width: 25px;

            &::after {
                overflow: hidden;
                display: inline-block;
                vertical-align: bottom;
                animation: ellipsis steps(4, end) 900ms infinite 1s;
                content: "\\2026";
                width: 0;
            }

            @keyframes ellipsis {
                to {
                    width: 1.25em;
                }
            }
        `,
    },
    errorText: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
    },
    pickupMessage: {
        size: "14px",
        lineHeight: "18px",
        weight: "normal",
        color: "text",
    },
    availabilityByWarehouseLink: {
        color: "text",
        css: css`
            font-size: 14px;
            line-height: 18px;
            font-weight: normal;
            text-decoration: underline;

            &.left-space {
                margin-left: 18px;
            }
        `,
    },
    separatorStyle: {
        css: css`
            margin-left: 10px;
            margin-right: 10px;
            line-height: 18px;
            @media only screen and (max-width: 769px) {
                display: none;
            }
        `,
    },
};

const InventoryMessage = styled.div<{ color: string }>`
    display: flex;
`;

const getAvailabilityColor = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return "common";
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return "warning";
    }

    // if (availabilityType === AvailabilityMessageType.LowStock) {
    //     return "warning";
    // }

    return "success";
};

const getAvailabilityIcon = (productSettings: ProductSettingsModel, availability?: AvailabilityDto) => {
    if (!productSettings.showInventoryAvailability) {
        return undefined;
    }

    const availabilityType = availability?.messageType;
    if (availabilityType === AvailabilityMessageType.OutOfStock) {
        return WarningStock;
    }

    return CheckmarkGylph;
};

const showLink = (
    availability: AvailabilityDto | undefined,
    trackInventory: boolean,
    productSettings: ProductSettingsModel,
    isProductDetailsPage?: boolean,
    additionalResults?: AdditionalResultsProps,
): boolean => {
    if (
        !availability ||
        availability.messageType === AvailabilityMessageType.NoMessage ||
        !trackInventory ||
        !productSettings.showInventoryAvailability ||
        !productSettings.displayInventoryPerWarehouse ||
        additionalResults?.hideCheckAllWarehouseHyperLink === "true"
    ) {
        return false;
    }

    if (!productSettings.displayInventoryPerWarehouseOnlyOnProductDetail) {
        return true;
    }

    return !!isProductDetailsPage;
};

const ProductAvailability = ({
    productId,
    availability,
    unitOfMeasure,
    trackInventory,
    productSettings,
    isProductDetailsPage,
    getRealTimeWarehouseInventory,
    failedToLoadInventory,
    extendedStyles,
    additionalResults,
    currentShipTo,
    customSettings,
    showInventory,
}: Props) => {
    const [modalIsOpen, setModalIsOpen] = useState(false);
    const [warehouses, setWarehouses] = useState<WarehouseDto[] | undefined>(undefined);
    const [errorMessage, setErrorMessage] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const modalCloseHandler = () => {
        setModalIsOpen(false);
    };
    const viewAvailabilityByWarehouseClickHandler = () => {
        setIsLoading(true);
        setModalIsOpen(true);
        getRealTimeWarehouseInventory({
            productId,
            unitOfMeasure,
            expand: ["warehouses"],
            onComplete: result => {
                setWarehouses(result.warehouses);
                setErrorMessage(result.errorMessage);
                setIsLoading(false);
            },
        });
    };

    const color = getAvailabilityColor(productSettings, availability);
    const iconSrc = getAvailabilityIcon(productSettings, availability);

    const [styles] = useState(() => mergeToNew(productAvailabilityStyles, extendedStyles));

    if (!showInventory) {
        return null;
    }
    
    if (failedToLoadInventory) {
        return (
            <StyledWrapper {...styles.errorWrapper}>
                <Typography {...styles.errorText}>{siteMessage("RealTimeInventory_InventoryLoadFailed")}</Typography>
            </StyledWrapper>
        );
    }
    const showWarehouseavailability =()=>{
        if((customSettings.statesToRestrictInventoryToDefaultWarehouse).includes(currentShipTo?.state?.abbreviation)){
            return false
        }
        return true
    }

    let inventoryTextComponent = <Typography {...styles.realTimeText} />;
    if (availability?.messageType !== AvailabilityMessageType.NoMessage && availability?.message?.length) {
        inventoryTextComponent = (
            <InventoryMessage color={color} {...styles.inventoryMessage}>
                <Icon
                    color={color}
                    src={iconSrc}
                    css={css`
                        width: 14px;
                        height: 14px;
                        color: ${color};
                        margin-right: 4px;
                        @media print {
                            width: 12px;
                            height: 12px;
                            margin-right: 2px;
                        }
                    `}
                />
                <Typography {...styles.messageText} data-test-selector="availabilityMessage">
                    {availability.message}
                </Typography>
            </InventoryMessage>
        );
    } else if (availability) {
        inventoryTextComponent = <></>;
    }

    let checkWarehousesModal = <></>;
    if (
        availability &&
        availability.message &&
        showLink(availability, trackInventory, productSettings, isProductDetailsPage, additionalResults)
    ) {
        checkWarehousesModal = (
            <>
                {additionalResults?.inventoryFallBack !== "true" && (
                    <Link
                        {...styles.availabilityByWarehouseLink}
                        onClick={viewAvailabilityByWarehouseClickHandler}
                        data-test-selector="availabilityByWarehouseLink"
                        className={additionalResults?.pickupMessage?.length ? "no-left-space" : "left-space"}
                    >
                        {availability?.messageType === AvailabilityMessageType.OutOfStock
                            ? translate("Check Lead Times")
                            : translate("Check All Warehouses")}
                    </Link>
                )}
                <Modal
                    {...styles.availabilityByWarehouseModal}
                    headline={translate("Warehouse Inventory")}
                    isOpen={modalIsOpen}
                    handleClose={modalCloseHandler}
                >
                    {isLoading && (
                        <StyledWrapper {...styles.centeringWrapper}>
                            <LoadingSpinner {...styles.spinner} />
                        </StyledWrapper>
                    )}
                    {!isLoading && (
                        <>
                            {errorMessage ? (
                                <div>{errorMessage}</div>
                            ) : (
                                    <DataTable
                                        {...styles.availabilityByWarehouseTable}
                                        data-test-selector="availabilityByWarehouseModal"
                                    >
                                        <DataTableHead {...styles.availabilityByWarehouseTableHead}>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Warehouse")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {`${translate("Qty On Hand")} / ${unitOfMeasure}`}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {translate("Typical Lead Time (Days)")}
                                            </DataTableHeader>
                                            <DataTableHeader {...styles.availabilityByWarehouseTableHeader}>
                                                {`${translate("Qty On Order")} / ${unitOfMeasure}`}
                                            </DataTableHeader>
                                        </DataTableHead>
                                        <DataTableBody {...styles.availabilityByWarehouseTableBody}>
                                            {warehouses?.map(warehouse => (
                                                <DataTableRow
                                                    {...styles.availabilityByWarehouseTableRow}
                                                    key={warehouse.name}
                                                >
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Description"
                                                    >
                                                        {warehouse.description || warehouse.name}
                                                    </DataTableCell>
                                                    <DataTableCell
                                                        {...styles.availabilityByWarehouseTableCell}
                                                        data-test-selector="warehouseAvailabilityModal_Quantity"
                                                    >
                                                        {warehouse.qty === 0 ? "-" : warehouse.qty}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                        {(warehouse as any)?.properties?.typicalLeadTime}
                                                    </DataTableCell>
                                                    <DataTableCell {...styles.availabilityByWarehouseTableCell}>
                                                        {(warehouse as any)?.properties?.qtyOnOrder === "0"
                                                            ? "-"
                                                            : (warehouse as any)?.properties?.qtyOnOrder}
                                                    </DataTableCell>
                                                </DataTableRow>
                                            ))}
                                        </DataTableBody>
                                    </DataTable>
                            )}
                        </>
                    )}
                </Modal>
            </>
        );
    }

    return (
        <>
            {additionalResults?.pickupMessage?.length ? (
                <StyledWrapper {...styles.containerWithAdditionalMessage}>
                    {showWarehouseavailability() && 
                    <>
                        <StyledWrapper>{inventoryTextComponent}</StyledWrapper>
                        <StyledWrapper className="separator" {...styles.separatorStyle}>
                            |
                        </StyledWrapper>
                    </>
                    }
                    <StyledWrapper {...styles.additionalMessageContainer}>
                        <StyledWrapper>
                            <Typography {...styles.pickupMessage}>{additionalResults?.pickupMessage}</Typography>
                        </StyledWrapper>
                    {showWarehouseavailability() &&
                        <StyledWrapper {...styles.availabilityByWarehouseLinkGridItem}>
                            {checkWarehousesModal}
                        </StyledWrapper>
                    }
                    </StyledWrapper>
                </StyledWrapper>
            ) : (
                <StyledWrapper {...styles.container}>
                    <StyledWrapper>{inventoryTextComponent}</StyledWrapper>
                    {showLink(availability, trackInventory, productSettings, isProductDetailsPage) && (
                        <StyledWrapper {...styles.availabilityByWarehouseLinkGridItem}>
                            {checkWarehousesModal}
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            )}
        </>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(ProductAvailability);
